<template>
  <div :class="heroSchema.mobileBgColor">
    <div
      class="hero-banner__container"
      v-if="$mq !== 'mobile'"
      :style="containerStyle"
      :class="heroSchema.layout"
    >
      <div
        class="hero-banner"
        :class="[layoutVertical($mq), textAlign, 'bg-light-grey']"
        :style="[backgroundStyles, layoutStyle]"
      >
        <div
          class="hero-banner__overlay"
          v-if="heroSchema.overlayEnable"
          :class="heroSchema.overlayColor"
          :style="overlayStyles"
        ></div>
        <video
          class="hero-banner__video"
          v-if="heroSchema.enableVideoBackground && heroSchema.videoUrl && canAutoPlay"
          :src="heroSchema.videoUrl"
          autoplay="true"
          loop="true"
          muted="true"
        ></video>
        <div
          class="hero-banner__content-container container"
          :class="[layoutHorizontal]"
          :style="contentContainerStyles"
        >
          <div
            class="hero-banner__content"
            :class="contentProperties.classes"
            :style="contentProperties.style"
          >
            <h4
              class="preheader hero-banner__preheading"
              v-if="heroSchema.preHeading"
              :class="heroSchema.preHeadingColor"
            >
              {{ heroSchema.preHeading }}
            </h4>
            <component
              class="hero-banner__heading"
              v-if="heroSchema.heading"
              :is="heroSchema.headingTag"
              :class="heroSchema.headingColor"
              >{{ heroSchema.heading }}</component
            >
            <h3
              class="subheader hero-banner__subheading"
              v-if="heroSchema.subHeading"
              :class="heroSchema.subHeadingColor"
            >
              {{ heroSchema.subHeading }}
            </h3>
          </div>
        </div>
        <div
          class="hero-banner__content-container container"
          :class="[layoutHorizontalCopy]"
        >
          <div
            class="hero-banner__content"
            :class="contentProperties.classes"
            :style="contentProperties.style"
          >
            <p
              class="body-md hero-banner__body-copy"
              v-if="heroSchema.copy"
              :class="heroSchema.copyColor"
            >
              {{ heroSchema.copy }}
            </p>
            <div class="hero-banner__cta-container" :style="ctaContainerStyle" :class="heroSchema.ctaAlignment">
              <div
                class="hero-banner__cta-wrapper"
                v-if="heroSchema.enableCta && heroSchema.ctaText"
              >
                <v-button
                  class="hero-banner__cta"
                  v-if="heroSchema.ctaLayout.includes('button')"
                  :href="heroSchema.ctaUrl"
                  :class="heroSchema.ctaLayout"
                  tag="a"
                  >{{ heroSchema.ctaText }}</v-button
                >
                <v-link
                  class="hero-banner__cta"
                  v-if="heroSchema.ctaLayout.includes('link')"
                  :href="heroSchema.ctaUrl"
                  :class="heroSchema.ctaLayout"
                  >{{ heroSchema.ctaText }}</v-link
                >
              </div>
              <div
                class="hero-banner__cta-wrapper"
                v-if="heroSchema.enableCta2 && heroSchema.ctaText2"
              >
                <v-button
                  class="hero-banner__cta"
                  v-if="heroSchema.ctaLayout2.includes('button')"
                  :href="heroSchema.ctaUrl2"
                  :class="heroSchema.ctaLayout2"
                  tag="a"
                  >{{ heroSchema.ctaText2 }}</v-button
                >
                <v-link
                  class="hero-banner__cta"
                  v-if="heroSchema.ctaLayout2.includes('link')"
                  :href="heroSchema.ctaUrl2"
                  :class="heroSchema.ctaLayout2"
                  >{{ heroSchema.ctaText2 }}</v-link
                >
              </div>
              <div
                class="hero-banner__cta-wrapper"
                v-if="heroSchema.enableCta3 && heroSchema.ctaText3"
              >
                <v-button
                  class="hero-banner__cta"
                  v-if="heroSchema.ctaLayout3.includes('button')"
                  :href="heroSchema.ctaUrl3"
                  :class="heroSchema.ctaLayout3"
                  tag="a"
                  >{{ heroSchema.ctaText3 }}</v-button
                >
                <v-link
                  class="hero-banner__cta"
                  v-if="heroSchema.ctaLayout3.includes('link')"
                  :href="heroSchema.ctaUrl3"
                  :class="heroSchema.ctaLayout3"
                  >{{ heroSchema.ctaText3 }}</v-link
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="hero-banner__container"
      v-else
      :style="containerStyle"
      ref="main"
    >
      <div
        class="hero-banner"
        :class="[
          ...textAlignMobile,
          layoutVertical($mq),
          heroSchema.mobileLayout
        ]"
        :style="minHeightStyleMobile"
      >
        <div
          class="hero-banner__image-mobile"
          :style="backgroundStylesMobile"
        ></div>
        <div
          class="hero-banner__overlay"
          v-if="isOverlayMobile"
          :class="heroSchema.overlayColor"
          :style="overlayStyles"
        ></div>
        <video
          class="hero-banner__video"
          v-if="heroSchema.enableVideoBackground && heroSchema.videoUrl && canAutoPlay"
          :src="heroSchema.videoUrl"
          autoplay="true"
          loop="true"
          muted="true"
        ></video>
        <div
          class="hero-banner__content"
          :class="[
            { [heroSchema.mobileBgColor]: heroSchema.mobileLayout === 'below' },
            contentProperties.classes
          ]"
          :style="contentProperties.style"
        >
          <h4
            class="preheader hero-banner__preheading"
            v-if="heroSchema.preHeading"
            :class="heroSchema.preHeadingColorMobile"
          >
            {{ heroSchema.preHeading }}
          </h4>
          <h2
            class="h1 hero-banner__heading"
            v-if="heroSchema.heading"
            :class="heroSchema.headingColorMobile"
          >
            {{ heroSchema.heading }}
          </h2>
          <h3
            class="subheader hero-banner__subheading"
            v-if="heroSchema.subHeading"
            :class="heroSchema.subHeadingColorMobile"
          >
            {{ heroSchema.subHeading }}
          </h3>
          <p
            class="body-md hero-banner__body-copy"
            v-if="heroSchema.copy"
            :class="heroSchema.copyColorMobile"
          >
            {{ heroSchema.copy }}
          </p>
          <div class="hero-banner__cta-container" :class="heroSchema.ctaAlignment">
            <div
              class="hero-banner__cta-wrapper"
              v-if="heroSchema.enableCta && heroSchema.ctaText"
            >
              <v-button
                class="hero-banner__cta"
                v-if="heroSchema.ctaLayout.includes('button')"
                :href="heroSchema.ctaUrl"
                :class="heroSchema.ctaLayout"
                tag="a"
                >{{ heroSchema.ctaText }}</v-button
              >
              <v-link
                class="hero-banner__cta"
                v-if="heroSchema.ctaLayout.includes('link')"
                :href="heroSchema.ctaUrl"
                :class="heroSchema.ctaLayout"
                >{{ heroSchema.ctaText }}</v-link
              >
            </div>
            <div
              class="hero-banner__cta-wrapper"
              v-if="heroSchema.enableCta2 && heroSchema.ctaText2"
            >
              <v-button
                class="hero-banner__cta"
                v-if="heroSchema.ctaLayout2.includes('button')"
                :href="heroSchema.ctaUrl2"
                :class="heroSchema.ctaLayout2"
                tag="a"
                >{{ heroSchema.ctaText2 }}</v-button
              >
              <v-link
                class="hero-banner__cta"
                v-if="heroSchema.ctaLayout2.includes('link')"
                :href="heroSchema.ctaUrl2"
                :class="heroSchema.ctaLayout2"
                >{{ heroSchema.ctaText2 }}</v-link
              >
            </div>
            <div
              class="hero-banner__cta-wrapper"
              v-if="heroSchema.enableCta3 && heroSchema.ctaText3"
            >
              <v-button
                class="hero-banner__cta"
                v-if="heroSchema.ctaLayout3.includes('button')"
                :href="heroSchema.ctaUrl3"
                :class="heroSchema.ctaLayout3"
                tag="a"
                >{{ heroSchema.ctaText3 }}</v-button
              >
              <v-link
                class="hero-banner__cta"
                v-if="heroSchema.ctaLayout3.includes('link')"
                :href="heroSchema.ctaUrl3"
                :class="heroSchema.ctaLayout3"
                >{{ heroSchema.ctaText3 }}</v-link
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { flexSwitch } from '@/scripts/helpers/flex.js'
import { textAlignSwitch } from '@/scripts/helpers/textAlign.js'
import { scrollToId } from '@/scripts/helpers/scroll.js'
import Icon from '@/scripts/components/basic/Icon.vue'
import VButton from '@/scripts/components/buttons/VButton.vue'
import VLink from '@/scripts/components/buttons/VLink.vue'
import { px } from '@/scripts/filters/string.js'
import canAutoPlay from 'can-autoplay'

export default {
  name: 'HeroBanner',
  components: {
    Icon,
    VButton,
    VLink
  },
  props: {
    heroSchema: {
      type: Object,
      default: () => ({
        layoutHorizontal: String,
        layoutVertical: String,
        mobileBgColor: String,
        enableScrollTrigger: Boolean,
        arrowColor: String,
        image: String,
        imageMobile: String,
        imageMobileEnable: Boolean,
        maxWidth: Number,
        maxWidthContent: Number,
        overlayEnable: Boolean,
        overlayColor: String,
        overlayOpacity: Number,
        preHeading: String,
        preHeadingColor: String,
        topMargin: Number,
        bottomMargin: Number,
        topMarginCtas: Number,
        heading: String,
        headingTag: String,
        headingColor: String,
        subHeading: String,
        subHeadingColor: String,
        copy: String,
        copyColor: String,
        enableCta: Boolean,
        ctaLayout: String,
        ctaText: String,
        ctaUrl: String,
        videoUrl: String,
        enableVideoBackground: Boolean
      })
    }
  },
  data () {
    return {
      overlayStyles: {
        opacity: `${this.heroSchema.overlayOpacity / 100}`
      },
      scrollTriggerIconStyles: {},
      canAutoPlay: false,
      noImage: typeof this.heroSchema.image === 'string' && this.heroSchema.image.indexOf('no-image') > -1
    }
  },
  computed: {
    layoutHorizontal () {
      return flexSwitch('justify', this.heroSchema.layoutHorizontal)
    },
    layoutHorizontalCopy () {
      return flexSwitch('justify', this.heroSchema.layoutHorizontalCopy)
    },
    textAlign () {
      return textAlignSwitch(this.heroSchema.layoutHorizontal)
    },
    textAlignMobile () {
      return [
        textAlignSwitch(this.heroSchema.mobileTextAlignment),
        this.heroSchema.mobileTextAlignment === 'center' &&
        this.heroSchema.mobileLayout === 'overlay'
          ? 'flex-align-center'
          : ''
      ]
    },
    backgroundStylesMobile () {
      if (this.heroSchema.imageMobileEnable) {
        return {
          backgroundImage: `url('${this.heroSchema.imageMobile}')`
        }
      } else {
        return {
          backgroundImage: `url('${this.heroSchema.image}')`
        }
      }
    },
    backgroundStyles () {
      if (!this.noImage) {
        return {
          backgroundImage: `url('${this.heroSchema.image}')`
        }
      }
    },
    contentProperties () {
      const style = this.heroSchema.maxWidthContent
        ? { maxWidth: px(this.heroSchema.maxWidthContent) }
        : undefined
      return {
        classes: undefined,
        style
      }
    },
    containerStyle () {
      const {
        heroSchema: { bottomMargin, topMargin, maxWidth }
      } = this
      if (this.$mq === 'mobile') {
        return {
          marginTop: px(topMargin / 2),
          marginBottom: px(bottomMargin / 2),
          maxWidth
        }
      }
      return {
        marginTop: px(topMargin),
        marginBottom: px(bottomMargin),
        maxWidth
      }
    },
    contentContainerStyles () {
      const {
        heroSchema: { marginBetween }
      } = this
      if (this.$mq === 'mobile') {
        return {}
      }
      return {
        marginBottom: px(marginBetween)
      }
    },
    layoutStyle () {
      return {
        minHeight: this.heroSchema.layout === 'short' ? '540px' : '720px'
      }
    },
    isOverlayMobile () {
      const { heroSchema } = this
      return !!(
        heroSchema.overlayEnable &&
        !heroSchema.enableCta &&
        heroSchema.mobileLayout === 'overlay'
      )
    },
    minHeightStyleMobile () {
      const { heroSchema } = this
      const {
        bvaccel: { header }
      } = window

      if (
        heroSchema.mobileLayout === 'below' &&
        header &&
        header.topOffsetMobile
      ) {
        // set too 100vh accounting for fixed header.
        return { minHeight: `calc(100vh - ${header.topOffsetMobile}px)` }
      }
    },
    ctaContainerStyle () {
      return { marginTop: px(this.heroSchema.topMarginCtas) }
    }
  },
  created () {
    if (this.heroSchema.enableVideoBackground && this.heroSchema.videoUrl) {
      canAutoPlay
      .video({ timeout: 5000, muted: true })
      .then(({ result, error }) => {
        if (result === false) {
          console.warn('Error did occur: ', error)
        }
        if (result === true) {
          this.canAutoPlay = true;
        }
      })
      .catch(err => {
        console.warn('Error determining autoplay capability', err)
      })
    }
  },
  methods: {
    // get the next section below this and smoothly scroll to it
    scrollTrigger (e) {
      const nextElement = document.querySelector('.shopify-section.hero-banner')
        .nextSibling
      if (nextElement) {
        const id = `#${nextElement.id}`
        scrollToId(e, id)
      }
    },
    layoutVertical ($breakpoint) {
      const isMobile = $breakpoint === 'mobile'
      const direction = 'justify'

      return flexSwitch(direction, this.heroSchema.layoutVertical)
    }
  }
}
</script>

<style scoped lang="scss">
.hero-banner {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 64px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  &__preheading,
  &__heading,
  &__subheading {
    margin-bottom: 4px;
  }
  @include desktop-up {
    &__heading,
    &__subheading {
      margin-bottom: 12px;
    }
    &__preheading {
      margin-bottom: 48px;
    }
  }
  &.overlay {
    min-height: 630px;
    .hero-banner {
      &__image-mobile {
        position: absolute;
        height: 100%;
        width: 100%;
      }
    }
  }
  &.below {
    min-height: 100vh;
    justify-content: flex-start;

    .hero-banner__content {
      margin: 0;
      max-width: none;
    }
  }
  @include mobile-only {
    position: auto;
    height: auto;
    flex-direction: column;
    padding: 0;
  }
  @include tablet-only {
    background-position: initial;
  }
  &__container {
    position: relative;
    margin: 0 auto;
  }
  &__content-container {
    display: flex;
    z-index: 21;
    @include desktop-up {
      width: 100%;
      @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
        max-width: none;
        width: auto;
      }
    }
  }
  &__content {
    display: inline-flex;
    flex-direction: column;
    max-width: 250px;
    width: 100%;
    z-index: 21;

    @include tablet-up {
      max-width: none;
    }
    @include desktop-up {
      max-width: 896px;
    }
    @include mobile-only {
      padding: 32px 14px;
      max-width: 300px !important;
      margin: 0 auto !important;
    }
  }
  &__body-copy {
    margin-top: 0;
    word-break: break-word;
  }
  &__cta-container {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    margin-top: 16px;
    @include desktop-up {
      flex-direction: row;
      margin-top: 54px;
    }
  }
  &__cta-wrapper {
    display: inline-block;
    margin-top: 16px;
    line-height: 1;
    @include desktop-up {
      margin-top: 0px;
      padding: 0 16px;
      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        padding-right: 0;
      }
    }
  }
  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  &__video {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &__image-mobile {
    position: relative;
    height: 400px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }
}
</style>
